import { Autocomplete, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import FlussNodePanelField from "./FlussNodePanelField";
import FlussNodePanelShell from "./FlussNodePanelShell";
import {
    linkNameFor,
    linkNamesInDiagram,
    panelNodeKindFor,
} from "./fluss-node-panel-utils";

interface LinkNodePanelProps {
    selection: FlussElementSelection;
    modeler: any;
    canViewAdvanced: boolean;
    readOnly: boolean;
    onChanged: () => void;
}

export const LinkNodePanel = ({
    selection,
    modeler,
    canViewAdvanced,
    readOnly,
    onChanged,
}: LinkNodePanelProps) => {
    const { t } = useTranslation();
    const kind = panelNodeKindFor(selection);
    const [name, setName] = useState(linkNameFor(selection.businessObject));
    const [inputValue, setInputValue] = useState(name);
    const suggestions = useMemo(
        () => linkNamesInDiagram(modeler),
        [modeler, selection.id]
    );

    useEffect(() => {
        const current = linkNameFor(selection.businessObject);
        setName(current);
        setInputValue(current);
    }, [selection.id, selection.businessObject]);

    const commit = (next: string) => {
        const trimmed = next.trim();
        setInputValue(trimmed);
        if (trimmed === name) return;
        setName(trimmed);
        modeler?.get("modeling")?.updateProperties(selection.element, { name: trimmed });
        onChanged();
    };

    return (
        <FlussNodePanelShell
            selection={selection}
            kind={kind}
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "info",
                label: t("fluss.node_panel.tab.info", { defaultValue: "Info" }),
                content: (
                    <Stack spacing={1.25}>
                        <FlussNodePanelField
                            label={t("fluss.node_panel.link.name", { defaultValue: "Link name" })}
                        >
                            <Autocomplete
                                freeSolo
                                size="small"
                                disabled={readOnly}
                                options={suggestions}
                                inputValue={inputValue}
                                onInputChange={(_, value) => setInputValue(value)}
                                onChange={(_, value) => commit(String(value ?? ""))}
                                onBlur={() => commit(inputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputProps={{
                                            ...params.inputProps,
                                            "aria-label": String(t("fluss.node_panel.link.name", {
                                                defaultValue: "Link name",
                                            })),
                                        }}
                                    />
                                )}
                            />
                        </FlussNodePanelField>
                        <Typography variant="caption" color="text.secondary">
                            {t("fluss.node_panel.link.hint", {
                                defaultValue:
                                    "Throw and catch are paired by this exact name, inside the same " +
                                    "subprocess. This is also the node label shown on the canvas.",
                            })}
                        </Typography>
                    </Stack>
                ),
            }]}
        />
    );
};

export default LinkNodePanel;
